import { Fragment, type ReactNode } from 'react';
import { parseRichText, type RichTextBlock, type RichTextInline } from '@ecom/contracts';
import { useT } from '@/i18n/use-t';
import type { Language } from '@/lib/content-pages';
import { cx } from '@/components/ui';

/**
 * A page read back the way a shopper will read it (ADR-058): the same parser
 * the storefront uses, drawn with the admin's tokens — title, "On this page"
 * once there are three sections, then the body. A link shows where it goes
 * rather than going there, so a click never leaves an unsaved page.
 */
export function PagePreview({
  title,
  body,
  language,
  updated,
  className,
}: {
  title: string;
  body: string;
  language: Language;
  /** "Last updated …", already worded. */
  updated?: string | null;
  className?: string;
}) {
  const t = useT();
  const blocks = parseRichText(body);
  const sections = blocks.filter(
    (block): block is Extract<RichTextBlock, { type: 'heading' }> => block.type === 'heading' && block.level === 2,
  );

  return (
    <article lang={language} dir={language === 'ar' ? 'rtl' : 'ltr'} className={cx('min-w-0', className)}>
      {title.trim() ? (
        <h1 className="text-xl font-bold text-ink">{title}</h1>
      ) : (
        <h1 className="text-xl font-bold text-ink-faint">{t('pageUntitled')}</h1>
      )}
      {updated ? <p className="mt-1 text-xs text-ink-faint">{updated}</p> : null}

      {sections.length >= 3 ? (
        <nav className="mt-5 rounded-card border border-line bg-surface-sunken/50 p-4">
          <p className="mb-2 text-xs font-semibold text-ink-faint">{t('pageOnThisPage')}</p>
          <ol className="flex flex-col gap-1.5 text-[13.5px] text-ink-muted">
            {sections.map((section) => (
              <li key={section.id}>{section.content.map((part) => part.text).join('')}</li>
            ))}
          </ol>
        </nav>
      ) : null}

      {blocks.length === 0 ? (
        <p className="mt-5 text-sm text-ink-faint">{t('pagePreviewEmpty')}</p>
      ) : (
        <div className="mt-5 text-[14.5px] leading-relaxed text-ink-muted">
          {blocks.map((block, index) => {
            switch (block.type) {
              case 'heading':
                return block.level === 2 ? (
                  <h2 key={index} className="mb-2 mt-7 text-base font-semibold text-ink first:mt-0">
                    {inlines(block.content)}
                  </h2>
                ) : (
                  <h3 key={index} className="mb-1.5 mt-5 text-[15px] font-semibold text-ink first:mt-0">
                    {inlines(block.content)}
                  </h3>
                );
              case 'paragraph':
                return (
                  <p key={index} className="mt-3 first:mt-0">
                    {block.lines.map((line, lineIndex) => (
                      <Fragment key={lineIndex}>
                        {lineIndex > 0 ? <br /> : null}
                        {inlines(line)}
                      </Fragment>
                    ))}
                  </p>
                );
              case 'list': {
                const items = block.items.map((item, itemIndex) => <li key={itemIndex}>{inlines(item)}</li>);
                return block.ordered ? (
                  <ol key={index} className="mt-3 list-decimal space-y-1.5 ps-5 first:mt-0">
                    {items}
                  </ol>
                ) : (
                  <ul key={index} className="mt-3 list-disc space-y-1.5 ps-5 first:mt-0">
                    {items}
                  </ul>
                );
              }
            }
          })}
        </div>
      )}
    </article>
  );
}

function inlines(parts: RichTextInline[]): ReactNode {
  return parts.map((part, index) => {
    if (part.type === 'text') return <Fragment key={index}>{part.text}</Fragment>;
    if (part.type === 'strong') {
      return (
        <strong key={index} className="font-semibold text-ink">
          {part.text}
        </strong>
      );
    }
    return (
      <span
        key={index}
        title={part.href}
        className="cursor-help font-medium text-brand-600 underline underline-offset-2"
      >
        {part.text}
      </span>
    );
  });
}
